import { useState } from 'react'
import { Shield, Lock, Eye, BarChart3, CheckCircle } from 'lucide-react'

export default function AppSecurityPage() {
  const [appSecurityStatus, setAppSecurityStatus] = useState('protected')

  const handleScanApp = () => {
    setAppSecurityStatus('scanning')
    setTimeout(() => {
      setAppSecurityStatus('protected')
    }, 3000)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">App Security</h1>
        <p className="text-blue-200">Protect This Application from Threats & Malware</p>
      </div>

      {/* Security Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`rounded-lg border-2 p-6 transition-all ${
          appSecurityStatus === 'protected' ? 'border-green-500 bg-green-500/10' : 'border-yellow-500 bg-yellow-500/10'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-white">Security Status</p>
            {appSecurityStatus === 'protected' ? (
              <CheckCircle size={24} className="text-green-500" />
            ) : (
              <Shield size={24} className="text-yellow-500" />
            )}
          </div>
          <p className="text-3xl font-bold text-white capitalize">{appSecurityStatus}</p>
          <p className="text-xs text-white/70 mt-2">Real-time Protection Active</p>
        </div>

        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200">Threats Blocked</p>
            <Shield size={20} className="text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-white">847</p>
          <p className="text-xs text-green-400 mt-2">✓ All Neutralized</p>
        </div>

        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200">Malware Scans</p>
            <BarChart3 size={20} className="text-purple-500" />
          </div>
          <p className="text-3xl font-bold text-white">12,450</p>
          <p className="text-xs text-blue-300 mt-2">Last 30 Days</p>
        </div>
      </div>

      {/* Security Features */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
            <Lock size={20} />
            <span>Real-time Protection</span>
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <span className="text-green-300">Malware Scanning</span>
              <CheckCircle size={20} className="text-green-500" />
            </div>
            <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <span className="text-green-300">Intrusion Detection</span>
              <CheckCircle size={20} className="text-green-500" />
            </div>
            <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <span className="text-green-300">Network Monitoring</span>
              <CheckCircle size={20} className="text-green-500" />
            </div>
            <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <span className="text-green-300">Behavioral Analysis</span>
              <CheckCircle size={20} className="text-green-500" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
            <Eye size={20} />
            <span>Advanced Monitoring</span>
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <span className="text-green-300">Input Validation</span>
              <CheckCircle size={20} className="text-green-500" />
            </div>
            <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <span className="text-green-300">Threat Intelligence</span>
              <CheckCircle size={20} className="text-green-500" />
            </div>
            <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <span className="text-green-300">Vulnerability Scanning</span>
              <CheckCircle size={20} className="text-green-500" />
            </div>
            <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <span className="text-green-300">API Rate Limiting</span>
              <CheckCircle size={20} className="text-green-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Scan Button */}
      <button
        onClick={handleScanApp}
        disabled={appSecurityStatus === 'scanning'}
        className="w-full px-6 py-3 bg-gradient-to-r from-yellow-600 to-orange-600 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-yellow-500/50 transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
      >
        <Shield size={20} />
        <span>{appSecurityStatus === 'scanning' ? 'Scanning...' : 'Run Full Security Scan'}</span>
      </button>
    </div>
  )
}
